/**
 * Ksting
 *
 * Copyright © 2017 Chris Andersson <chris@wbisbi.com>. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React, { Component } from 'react';
import { object, string, bool, array, func } from 'prop-types';
import classNames from 'clsx';

import Button from 'components/Button/Button';
import TagPicker from 'components/TagPicker/TagPicker';
import Checkbox from 'components/Form/Input/Checkbox/Checkbox';

import p from 'polyglot/polyglot';

import styles from './edit-tags.scss';

/**
 * This is the EditTags component.
 *
 * @author Chris Andersson <chris@wbisbi.com>
 */
export default class EditTags extends Component {
  /**
   * Declare expected prop types.
   *
   * @type {object}
   */
  static propTypes = {
    user: object.isRequired,
    type: string.isRequired,
    rates: array.isRequired,
    talentTypes: array.isRequired,
    submitButtonText: string,
    compact: bool,
    onSubmit: func,
  };

  static defaultProps = {
    submitButtonText: null,
    compact: false,
    onSubmit: () => {},
  };

  /**
   * Create EditTags.
   *
   * @param {Object} props
   */
  constructor(props) {
    super(props);

    const {
      type,
      rates,
      talentTypes,
      user: { roles },
    } = this.props;
    const { talent_rates, talent_types } = roles && roles[type] || {} ;

    const userRates = talent_rates?.split(',');
    const userTypes = talent_types?.split(',');

    this.state = {
      selectedRates: talent_rates ? this.mapTags(userRates, rates) : [],
      selectedTalentTypes: talent_types
        ? this.mapTags(userTypes, talentTypes)
        : [],
    };
  }

  mapTags(items, options) {
    return items.map(item => options.find(({ value }) => item === value));
  }

  submitHandler = event => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const { selectedRates, selectedTalentTypes } = this.state;
    const {
      user: { loading },
      type: role_type,
      onSubmit,
    } = this.props;

    if (loading) return;

    const rates =
      selectedRates.length > 0 ? selectedRates.map(({ value }) => value) : [];
    const talent_rates = rates.join(',');

    const talentTypes =
      selectedTalentTypes.length > 0
        ? selectedTalentTypes.map(({ value }) => value)
        : [];
    const talent_types = talentTypes.join(',');

    onSubmit({
      role_type,
      talent_rates,
      talent_types,
    });
  };

  onChangeIncognito = event => {
    const { onSubmit } = this.props;
    const incognitoLock = event.target.checked;

    // this.setState({ incognitoLock });

    onSubmit({
      admin_incognito_lock: incognitoLock,
    });
  };

  onChangeShowOnLandingPage = event => {
    const { onSubmit } = this.props;
    const showOnLandingPage = event.target.checked;

    // this.setState({ showOnLandingPage });

    onSubmit({
      admin_show_on_landing_page: showOnLandingPage,
    });
  };

  onChangeRates = selectedRates => {
    this.setState({ selectedRates });
  };

  onChangeTalentTypes = selectedTalentTypes => {
    this.setState({ selectedTalentTypes });
  };

  /**
   * Render EditTags.
   *
   * @return {Object}
   */
  render() {
    const { selectedRates, selectedTalentTypes } = this.state;
    const {
      user: {
        loading,
        admin_incognito_lock: incognitoLock,
        admin_show_on_landing_page: showOnLandingPage,
      },
      type,
      rates,
      talentTypes,
      submitButtonText,
      compact,
    } = this.props;

    return (
      <div
        className={classNames(styles.wrapper, {
          [styles.compact]: compact,
        })}
      >
        {!compact && (
          <h3 className={styles.title}>
            {p.t(`account.admin.edit_tags.types.${type.toLowerCase()}`)}
          </h3>
        )}

        <form
          className={classNames(styles.form, {
            [styles.loading]: loading,
          })}
          onSubmit={this.submitHandler}
          noValidate
          autoComplete="off"
          ref={ref => {
            this.form = ref;
          }}
        >
          <div className={styles.checkbox}>
            <label htmlFor="admin_incognito_lock">
              <Checkbox
                name="admin_incognito_lock"
                value={incognitoLock}
                onChange={this.onChangeIncognito}
              />

              <p className={styles.label}>
                {p.t('account.admin.edit_tags.form.incognito.label')}
              </p>
            </label>
          </div>
          <div className={styles.checkbox}>
            <label htmlFor="admin_show_on_landing_page">
              <Checkbox
                name="admin_show_on_landing_page"
                value={showOnLandingPage}
                onChange={this.onChangeShowOnLandingPage}
              />

              <p className={styles.label}>
                {p.t('account.admin.edit_tags.form.show_on_landing_page.label')}
              </p>
            </label>
          </div>
          <div className={styles.tags}>
            <p className={styles.label}>
              {p.t('account.admin.edit_tags.form.rates.label')}
            </p>
            <TagPicker
              value={selectedRates}
              options={rates}
              onChange={this.onChangeRates}
              creatable={false}
              openMenuOnClick
            />
          </div>
          <div className={styles.tags}>
            <p className={styles.label}>
              {p.t('account.admin.edit_tags.form.talent_types.label')}
            </p>
            <TagPicker
              value={selectedTalentTypes}
              options={talentTypes}
              onChange={this.onChangeTalentTypes}
              creatable={false}
              openMenuOnClick
            />
          </div>

          <footer className={styles.footer}>
            <Button small={!compact} onClick={this.submitHandler}>
              {!submitButtonText && p.t('account.admin.edit_tags.form.submit')}
              {submitButtonText && submitButtonText}
            </Button>
          </footer>
        </form>
      </div>
    );
  }
}
